/**
 * 注册页面
 */

import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Divider } from 'antd';
import { UserOutlined, LockOutlined, TeamOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { register } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css'; // 复用登录页样式

const { Title, Text } = Typography;

interface RegisterForm {
    username: string;
    password: string;
    confirmPassword: string;
    invitationCode?: string;
}

const Register: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth(); // 使用 Context 的 login 来更新状态 (虽然 register 接口已经 set-cookie，但我们需要更新前端 context)

    const handleSubmit = async (values: RegisterForm) => {
        if (values.password !== values.confirmPassword) {
            message.error('两次输入的密码不一致');
            return;
        }

        setLoading(true);
        try {
            const response = await register(
                values.username,
                values.password,
                values.invitationCode
            );

            if (response.success) {
                message.success('注册成功！');

                // 注册成功后，后端已经自动登录（set-cookie），
                // 我们调用前端 context 的 checkAuth 或 login 来同步状态
                // 简单起见，我们直接利用 password 再调一次 context login 确保状态同步
                await login(values.username, values.password);

                navigate('/');
            }
        } catch (error: any) {
            console.error('注册失败:', error);
            const errorMsg = error.response?.data?.error || '注册失败，请稍后重试';
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <Card className="login-card">
                <div className="login-header">
                    <Title level={2} className="login-title">
                        注册新账号
                    </Title>
                    <Text type="secondary">加入团队或创建新组织</Text>
                </div>

                <Form
                    name="register"
                    onFinish={handleSubmit}
                    autoComplete="off"
                    size="large"
                >
                    <Form.Item
                        name="username"
                        rules={[{ required: true, message: '请输入用户名' }]}
                    >
                        <Input
                            prefix={<UserOutlined />}
                            placeholder="用户名"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{ required: true, message: '请输入密码' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="密码"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        rules={[{ required: true, message: '请确认密码' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="确认密码"
                        />
                    </Form.Item>

                    <Divider orientation="center" style={{ fontSize: '12px', color: '#999', margin: '12px 0' }}>
                        可选
                    </Divider>

                    <Form.Item
                        name="invitationCode"
                        extra="输入邀请码将自动加入对应组织，留空则创建新组织"
                    >
                        <Input
                            prefix={<TeamOutlined />}
                            placeholder="组织邀请码 (如: SMART-1234)"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                        >
                            注册并登录
                        </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center' }}>
                        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/login')}>
                            返回登录
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default Register;
